import { useQuery, useSubscription } from '@apollo/client'
import { GET_CALENDAR_EVENT_QUERY, GET_CALENDAR_EVENTS_QUERY, SUBSCRIBE_TO_USER_CALENDAR_UPDATES } from 'src/graphql/queries'
import { useTheme } from '@mui/material'
import { useCalendarColors } from 'src/theme/calendar'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { useDebounceCallback } from 'usehooks-ts'
import { ICalendarEvent } from '../types'

export function useCalendarEvent(eventId: string) {
  const { data, loading, refetch } = useQuery(GET_CALENDAR_EVENT_QUERY, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
    variables: { id: eventId },
  })

  const theme = useTheme()
  const { calendarColorOptions } = useCalendarColors()

  const externalEvent: ICalendarEvent | null = useMemo(
    () => {
      const event = data?.getCalendarEvent
      if (!event) {
        return null
      }
      const eventColor = calendarColorOptions[0]
      const eventTextColor = theme.palette.getContrastText(eventColor)
      return {
        id: event.id,
        meetingId: event.meetingId,
        title: event.title,
        allDay: false,
        description: event.meetingHtmlBody ?? '',
        color: eventColor,
        textColor: eventTextColor,
        createdAt: event.createdAt,
        updatedAt: event.updatedAt,
        start: event.startsAt,
        end: event.endsAt,
        meetingPlatform: event.meetingPlatform,
        meetingUrl: event.meetingUrl,
        recordMeeting: event.recordMeeting,
        meetingRecorders: event.meetingRecorders,
        attendeeStatuses: event.attendeeStatuses
      }
    },
    [calendarColorOptions, theme.palette, data]
  )

  return { event: externalEvent, loading, refetch }
}


export function useCalendarEventRange(rangeStart: Date, rangeEnd: Date, meetingSeriesId?: string) {
  const theme = useTheme()
  const { calendarColorOptions } = useCalendarColors()
  const [, setDataRange] = useState<{rangeStart: number, rangeEnd: number} | null>(null)
  const [events, setEvents] = useState<ICalendarEvent[]>([])
  const [internalRangeStart, setInternalRangeStart] = useState<Date | null>(null)
  const [internalRangeEnd, setInternalRangeEnd] = useState<Date | null>(null)
  const { loading, refetch } = useQuery(GET_CALENDAR_EVENTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !internalRangeStart || !internalRangeEnd,
    variables: {
      rangeStart: internalRangeStart,
      rangeEnd: internalRangeEnd,
      meetingSeriesId,
    },
    onCompleted: (data) => {
      if (!data.calendarEvents || !data.calendarEvents.rangeStart || !data.calendarEvents.rangeEnd) return
      setDataRange({ rangeStart: new Date(data.calendarEvents.rangeStart).getTime(), rangeEnd: new Date(data.calendarEvents.rangeEnd).getTime() })
      setEvents(data.calendarEvents.events.map((event) => {
        const eventColor = calendarColorOptions[0]
        const eventTextColor = theme.palette.getContrastText(eventColor)
        return {
            id: event.id,
          meetingId: event.meetingId,
          title: event.title,
          allDay: false,
          description: event.meetingHtmlBody ?? '',
          color: eventColor,
          textColor: eventTextColor,
          createdAt: event.createdAt,
          updatedAt: event.updatedAt,
          start: event.startsAt,
          end: event.endsAt,
          meetingPlatform: event.meetingPlatform,
          meetingUrl: event.meetingUrl,
          recordMeeting: event.recordMeeting,
          meetingRecorders: event.meetingRecorders,
          attendeeStatuses: event.attendeeStatuses
        }
      }))
    }
  })

  const subscriptionCallback = useDebounceCallback(useCallback(() => {
    refetch()
  }, [refetch]), 5000, {
    leading: true,
  })
  useSubscription(SUBSCRIBE_TO_USER_CALENDAR_UPDATES, {
    onData: (data) => {
      const updates = data.data?.data?.userCalendarUpdates
      if (!updates) return

      const { earliestEventStart, latestEventEnd } = updates

      // turn into dates
      const earliestEventStartDate = new Date(earliestEventStart)
      const latestEventEndDate = new Date(latestEventEnd)
      // if the events are within the current range, refetch
      if (earliestEventStartDate && (earliestEventStartDate >= rangeStart && earliestEventStartDate <= rangeEnd)) {
        subscriptionCallback()
      } else if (latestEventEndDate && (latestEventEndDate >= rangeStart && latestEventEndDate <= rangeEnd)) {
        subscriptionCallback()
      }
    }
  })

  useEffect(() => {
    if (rangeStart && rangeEnd) {
      setInternalRangeStart(rangeStart)
      setInternalRangeEnd(rangeEnd)
    }
  }, [rangeStart, rangeEnd])

  return { events, loading, refetch }
}
